import { _ } from "../../core/polyglot/polyglot.js";
import Timespan from "./Timespan.js";

/**
 * `period` is the common superclass for all period
 * types. Instances should not be created by hand, but only
 * looked up from domain objects.
 *
 * @param {{}} spec - Parameter object
 * @param {fiscalYear} spec.year - Fiscal year to which the period belongs
 * @param {integer} spec.periodIndex - Index of the period within its fiscal year
 * @param {{}} my - protected fields holder
 * @return {*} period
 */
export default class Period extends Timespan {
	constructor({ year, periodIndex } = {}) {
		super(...arguments);

		// We need this because of Highcharts. Highcharts has a
		// merge() function which iterates over all properties of an
		// object passed as parameter, and all properties of each
		// property recursively. As there doesn't seem to be any
		// protection against cycles in merge(), this function
		// triggers a stack overflow. To avoid the stack overflow, we
		// hide the _year property (which typically introduces a
		// cycle) from Highcharts by making it non-enumerable.
		//
		// https://github.com/highcharts/highcharts/issues/6046
		Object.defineProperty(this, "_year", {
			enumerable: false,
			value: year,
			writable: true,
		});

		this._periodIndex = periodIndex || 1;
		this._name = "";
		this._periodId = this._name;
	}

	getName() {
		return this._name;
	}

	getYear() {
		return this._year;
	}

	getYearId() {
		return this.getYear().getId();
	}

	setYear(newYear) {
		this._year = newYear;
	}

	getPeriodType() {
		return this.constructor.getType();
	}

	getGroupLabel() {
		return "";
	}

	getPeriodId() {
		return this._periodId;
	}

	getPeriodIndex() {
		return this._periodIndex;
	}

	hasSamePeriodIndex(period) {
		return period.getPeriodIndex() === this._periodIndex;
	}

	getDimensionTree() {
		return this.getYear().getDimensionTree();
	}

	numberOfDays() {
		return this.asDays().length;
	}

	/**
	 * Returns the first equivalent period of `periodType` type.
	 * e.g., if `this` is a Month of June 2024,
	 * `asPeriodType("Quarter")` will return Q2 2024.
	 * In return, Q2 2024 `asPeriodType("Month")` will return
	 * April 2024.
	 * Check {@link PeriodType} ids for the different values of
	 * periodType.
	 *
	 * @param {string} periodType - Type id that this Period
	 *   should be converted to
	 */
	asPeriodType(periodType) {
		const periods = this._year.asPeriods(periodType);
		return periods.find((period) => this.includesAnyOf(period));
	}

	asDays() {
		return this._filterPeriods(this._year.asDays());
	}

	asWeeks() {
		return this._filterPeriods(this._year.asWeeks());
	}

	asMonths() {
		return this._filterPeriods(this._year.asMonths());
	}

	lastMonth() {
		return this.asMonths().at(-1);
	}

	asTimespan() {
		return new Timespan({
			start: this.getStart(),
			end: this.getEnd(),
		});
	}

	asQuarters() {
		return this._filterPeriods(this._year.asQuarters());
	}

	asHalfYears() {
		return this._filterPeriods(this._year.asHalfYears());
	}

	asYears() {
		return this._filterPeriods(this._year.asYears());
	}

	isNullPeriod() {
		return false;
	}

	isCustomPeriod() {
		return false;
	}

	isConsecutiveMonths() {
		return false;
	}

	isWeek() {
		return false;
	}

	isMonth() {
		return false;
	}

	isQuarter() {
		return false;
	}

	isBimester() {
		return false;
	}

	isHalfYear() {
		return false;
	}

	isYear() {
		return false;
	}

	toString() {
		return `${this.getPeriodType()}-${this.getPeriodIndex()}-${this._year.getName()}`;
	}

	toSmartString() {
		if (!this.getName()) {
			return _("n/a");
		}

		return `${this.getName()} ${this.getYearNameForPeriod()}`;
	}

	getYearNameForPeriod() {
		if (this._start.getYear() === this._end.getYear()) {
			return this._start.getFullYear().toString();
		}
		return this._year.getName();
	}

	/**
	 * Given a list of an array of `previousYears`, return the
	 * same calendar periods in each previous year.
	 */
	getSameCalendarPeriodsInPreviousYears(previousYears) {
		const sortedYears = previousYears.sort((year1, year2) =>
			year1.endsBefore(year2) ? 1 : -1,
		);
		const currentYear = this.getYear();
		let previousPeriods = [];

		sortedYears.forEach((year) => {
			if (!year.endsBefore(currentYear)) {
				return;
			}
			const samePeriodInPreviousYear = year.findSameCalendarPeriods(this);
			previousPeriods = previousPeriods.concat(samePeriodInPreviousYear);
		});

		return previousPeriods;
	}

	equals(candidate) {
		if (!candidate) {
			return false;
		}
		if (!candidate.getYear() && this._year) {
			return false;
		}

		return (
			candidate.getPeriodType() === this.getPeriodType() &&
			candidate.getPeriodIndex() === this.getPeriodIndex() &&
			candidate.getYear().getId() === this._year.getId()
		);
	}

	isActive() {
		return this._year.isWithinActivePeriod(this);
	}

	numberOfPeriodsSince(period) {
		if (this.getStart() < period.getStart()) {
			return this._numberOfPeriodsBetween(this, period);
		}
		return this._numberOfPeriodsBetween(period, this);
	}

	getAccounts() {
		return this._year.getAccounts();
	}

	_numberOfPeriodsBetween(start, end) {
		const periodLength = end.getEnd().valueOf() - end.getStart().valueOf();
		const totalLength = end.getEnd().valueOf() - start.getStart().valueOf();
		return Math.floor(totalLength / periodLength);
	}

	_filterPeriods(periods) {
		return periods.filter((period) => this.includesAllOf(period));
	}

	static getType() {
		return "";
	}
}
